/**
 * Aba "Coloração - Ficha Atendimento" da ficha da cliente, idêntica à do legado
 * (`admin.html:2646-2727`, `5166-5243`, `5273-5337`; linhas 200 a 206 do inventário, e a trava de
 * botão da linha 179).
 *
 * Nove etapas: 1 a 5 e 8 de marcar, 6 em quatro campos na mesma linha, 7 em linhas de comparativo,
 * 9 com a estação e o resultado; o texto explicativo das 12 estações; e o salvar com "Salvando...",
 * "Salvo!" (some em 3 s) ou "Erro ao salvar: " com a mensagem crua.
 *
 * Território da frente A7. A moldura importa esta aba pelo nome `AbaColoracao`.
 */

// Estado, efeito, memória e identificadores do React.
import { useCallback, useEffect, useId, useRef, useState } from 'react';
// As peças do Chassi.
import { Botao, Campo, CampoTexto, Selecao } from '@complexity/ui';
// A leitura e a gravação da ficha de coloração (A2).
import { lerColoracao, salvarColoracao } from '@studiolu/dados/areas/ficha';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O parágrafo do status, peça comum (o texto em si vive com a página, ver `status-da-pagina.ts`).
import { LinhaDeStatus } from '../../../comum/status-temporario.js';
// O contrato da aba e o texto de "Última atualização em ...".
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
import { textoUltimaAtualizacao } from '../comum-ficha/regras.js';
// As caixas de marcar da ficha (A2).
import { alternarMarcado, GrupoDeMarcacao, opcoesDe } from '../comum-ficha/Marcacoes.js';
// Os catálogos fixos das etapas (A2).
import {
  ESTACOES_12,
  ETAPAS_COLORACAO_CHECKBOX,
  FAMILIAS_CORES_COLORACAO,
  OPCOES_ESTACAO_SIMPLES,
} from '../constantes.js';
// O escopo estável, o recorte por papel e o aviso de ficha ausente, das abas da ficha (A3).
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';
// As regras desta aba e as duas partes dela.
import {
  CARACTERISTICAS,
  formularioVazio,
  leituraQuebraNoLegado,
  lerLinhaComoOLegado,
  registroDoFormulario,
  type CampoDeCaracteristica,
  type FormularioColoracao,
} from './coloracao/regras.js';
import { Comparativos } from './coloracao/Comparativos.js';
import { TextoExplicativo } from './coloracao/TextoExplicativo.js';
// O status e o acordeão que vivem com a página, e não com a aba (achados da verificação da A7).
import { criarStatusDaPagina, useStatusDaPagina } from './coloracao/status-da-pagina.js';
import { useAcordeaoExplicativo } from './coloracao/acordeao.js';

/**
 * "(selecione)" e as 12 estações, na ordem dos `option` do legado (`admin.html:5211-5212`).
 *
 * "(selecione)" entra como OPÇÃO COMUM de valor vazio, e não pela propriedade `vazio` da `Selecao`,
 * por um motivo medido: a `Selecao` desenha `vazio ?? ''` quando o valor não está na lista
 * (`packages/ui/src/components/Selecao.tsx:193`). Com o `vazio`, uma estação gravada fora das 12
 * apareceria como "(selecione)"; sem ele, o gatilho fica EM BRANCO, que é o que o `select` do legado
 * mostra (`selectedIndex -1`). A lista aberta é a mesma nos dois jeitos (`Selecao.tsx:173-175`).
 */
const OPCOES_ESTACOES = [{ valor: '', rotulo: '(selecione)' }, ...ESTACOES_12.map((e) => ({ valor: e, rotulo: e }))];
// O `#status-coloracao` do legado (`admin.html:2727`): um só, criado com o módulo e vivo com a página.
const statusColoracao = criarStatusDaPagina();
// As quatro estações como opções de marcar, iguais em toda família de cor.
const OPCOES_FAMILIA = opcoesDe(OPCOES_ESTACAO_SIMPLES);

export function AbaColoracao({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Coloração - Ficha Atendimento" />;
  return <ConteudoColoracao ficha={ficha} />;
}

function ConteudoColoracao({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { somenteLeitura } = useRecortePorPapel();
  const escopo = useEscopoEstavel(ficha);
  // O contador das chaves das linhas da Etapa 7: cresce sempre, para chave removida nunca voltar.
  const contador = useRef(0);
  const novaChave = useCallback(() => ++contador.current, []);
  const [formulario, setFormulario] = useState<FormularioColoracao>(() => formularioVazio(novaChave));
  // O "Salvar" nasce travado e só destrava depois da leitura (linha 179, `admin.html:5976` e
  // `5305`): salvar antes gravaria o que estivesse na tela, de outra cliente.
  const [carregado, setCarregado] = useState(false);
  const status = useStatusDaPagina(statusColoracao);
  // O acordeão do texto explicativo, aberto ou fechado como a pessoa deixou (ver `acordeao.ts`).
  const [acordeaoAberto, mudarAcordeao] = useAcordeaoExplicativo(ficha);
  // O identificador do rótulo do grupo da etapa 8.
  const idEtapa8 = useId();

  // A leitura ao abrir a aba, como o `carregarColoracao` do legado (`admin.html:5273-5306`).
  useEffect(() => {
    // Uma resposta que chega depois de a aba trocar de cliente não escreve na tela.
    let valendo = true;
    setCarregado(false);
    // O legado limpa o status antes de ler (`5275`). Só este ponto e o salvar escrevem nele: uma
    // gravação ainda em curso que responder depois daqui escreve por cima, como no legado.
    statusColoracao.escrever('');
    void lerColoracao(dados, escopo).then((resultado) => {
      if (!valendo) return;
      // DEFEITO DO LEGADO REPRODUZIDO: falha na leitura só vai para o console, o formulário volta
      // vazio e o "Salvar" destrava, então salvar gravaria o vazio por cima (`5277`, `5303`).
      if (!resultado.ok) console.error('Erro ao carregar ficha de coloração:', resultado.erro.mensagem);
      const linha = resultado.ok ? resultado.dados : null;
      // O que está na tela entra como `anterior`: é dele que a leitura quebrada guarda a estação e o
      // resultado final (ver `leituraQuebraNoLegado`).
      setFormulario((anterior) => lerLinhaComoOLegado(linha, anterior, novaChave));
      // DEFEITO DO LEGADO REPRODUZIDO: a leitura para no `forEach` dos comparativos, sem o
      // "Última atualização em ..." e sem destravar o "Salvar" (`5237`, `5303`). O legado deixa a
      // exceção solta no console; aqui ela é escrita lá com o mesmo texto do Chrome.
      if (leituraQuebraNoLegado(linha)) {
        console.error('Erro ao carregar ficha de coloração: TypeError: itens.forEach is not a function');
        return;
      }
      // "Última atualização em ..." só quando a linha tem a data (`5300-5302`).
      if (linha?.atualizado_em) statusColoracao.escrever(textoUltimaAtualizacao(linha.atualizado_em));
      setCarregado(true);
    });
    return () => {
      valendo = false;
    };
  }, [dados, escopo, novaChave]);

  // Marca ou desmarca uma opção de um grupo, guardando na ordem das opções da tela.
  const alternar = (campo: string, opcoes: readonly string[], valor: string, marcado: boolean) =>
    setFormulario((f) => ({
      ...f,
      marcadas: { ...f.marcadas, [campo]: alternarMarcado(f.marcadas[campo] ?? [], opcoes, valor, marcado) },
    }));

  // Troca o texto de uma característica da Etapa 6.
  const mudarCaracteristica = (campo: CampoDeCaracteristica, valor: string) =>
    setFormulario((f) => ({ ...f, [campo]: valor }));

  // "Salvar ficha de coloração" (`admin.html:5306-5337`). A resposta escreve no parágrafo da
  // página mesmo com a aba fora da tela, como o legado escreve no nó do DOM (`5335`).
  const salvar = async () => {
    statusColoracao.escrever('Salvando...');
    // O registro sai do que está na tela no instante do clique, como o legado lê o DOM.
    const resultado = await salvarColoracao(dados, escopo, registroDoFormulario(formulario, new Date().toISOString()));
    // Este ponto do legado mostra a mensagem CRUA depois do prefixo, e não a tradução.
    if (!resultado.ok) {
      statusColoracao.escrever('Erro ao salvar: ' + resultado.erro.mensagem);
      return;
    }
    statusColoracao.mostrar('Salvo!');
  };

  return (
    <section className="flex flex-col gap-cx-3" data-testid="aba-coloracao">
      <h3 className="text-cx-base font-semibold text-texto">Coloração - Ficha Atendimento</h3>

      <h4 className="mt-cx-4 text-cx-base font-medium text-texto">Avaliação Inicial (Etapas 1 a 5)</h4>
      <p className="text-cx-sm text-texto-suave">Selecione um ou mais.</p>
      {ETAPAS_COLORACAO_CHECKBOX.map((etapa) => (
        <GrupoDeMarcacao
          key={etapa.campo}
          titulo={etapa.titulo}
          opcoes={opcoesDe(etapa.opcoes)}
          marcadas={(valor) => (formulario.marcadas[etapa.campo] ?? []).includes(valor)}
          aoMudar={(valor, marcado) => alternar(etapa.campo, etapa.opcoes, valor, marcado)}
          // Lado a lado com quebra, como o `flex-direction:row; flex-wrap:wrap` do legado (`5197`).
          emLinha
          // Consulta: caixa desabilitada (`admin.html:3394`).
          desabilitado={somenteLeitura}
        />
      ))}

      <h4 className="mt-cx-4 text-cx-base font-medium text-texto">
        Registro de Características e Estações (Etapas 6 e 7)
      </h4>
      <p className="text-cx-sm text-texto-suave">Espaço para registrar as características determinadas para o momento.</p>
      {/* Os quatro campos curtos na mesma linha, como a `.linha-compacta` do legado (`2653`). O arranjo é o
          da `LinhaDeCampos` do Chassi (`packages/layout`, que o app não importa, ADR 0041): quebra pela
          largura real e alinha pelo topo. */}
      <div className="flex flex-wrap items-start gap-cx-2">
        {CARACTERISTICAS.map(({ campo, rotulo }) => (
          <Campo
            key={campo}
            rotulo={rotulo}
            type="text"
            value={formulario[campo]}
            disabled={somenteLeitura}
            onChange={(evento) => mudarCaracteristica(campo, evento.target.value)}
          />
        ))}
      </div>
      <Comparativos
        linhas={formulario.comparativos}
        somenteLeitura={somenteLeitura}
        aoMudar={(chave, campo, valor) =>
          setFormulario((f) => ({
            ...f,
            comparativos: f.comparativos.map((l) => (l.chave === chave ? { ...l, [campo]: valor } : l)),
          }))
        }
        aoRemover={(chave) =>
          setFormulario((f) => ({ ...f, comparativos: f.comparativos.filter((l) => l.chave !== chave) }))
        }
        aoAdicionar={() =>
          setFormulario((f) => ({
            ...f,
            comparativos: [...f.comparativos, { chave: novaChave(), campo1: '', campo2: '', resultado: '' }],
          }))
        }
      />

      <h4 className="mt-cx-4 text-cx-base font-medium text-texto">Comparativo de Cores e Resultado Final (Etapas 8 e 9)</h4>
      {/* O rótulo da etapa 8 é um `label` sem `for` no legado (`2684`): aqui ele nomeia o grupo. */}
      <div role="group" aria-labelledby={idEtapa8} className="flex flex-col gap-cx-2">
        <p id={idEtapa8} className="text-cx-sm font-medium text-texto">
          Etapa 8 | Comparativo de Cores
        </p>
        <p className="text-cx-sm text-texto-suave">
          Teste por famílias de cores, onde se avalia qual estação (Primavera, Outono, Inverno ou Verão) performa
          melhor para cada tom. Selecione um ou mais.
        </p>
        {FAMILIAS_CORES_COLORACAO.map((familia) => (
          <GrupoDeMarcacao
            key={familia.campo}
            titulo={familia.titulo}
            opcoes={OPCOES_FAMILIA}
            marcadas={(valor) => (formulario.marcadas[familia.campo] ?? []).includes(valor)}
            aoMudar={(valor, marcado) => alternar(familia.campo, OPCOES_ESTACAO_SIMPLES, valor, marcado)}
            emLinha
            desabilitado={somenteLeitura}
          />
        ))}
      </div>

      {/* "(selecione)" e as 12 estações, na ordem do legado (`5211-5212`); sem `vazio`, ver `OPCOES_ESTACOES`. */}
      <Selecao
        rotulo="Etapa 9 | Defina a Estação"
        opcoes={OPCOES_ESTACOES}
        value={formulario.estacao_definida}
        disabled={somenteLeitura}
        onChange={(evento) => {
          // O valor lido antes do `setState`, porque a função de atualização roda depois do evento.
          const valor = evento.target.value;
          setFormulario((f) => ({ ...f, estacao_definida: valor }));
        }}
      />
      <CampoTexto
        rotulo="Resultado Final"
        linhas={3}
        placeholder="Descreva o resultado final da análise..."
        value={formulario.resultado_final}
        disabled={somenteLeitura}
        onChange={(evento) => {
          // O valor lido antes do `setState`, pelo mesmo motivo da estação.
          const valor = evento.target.value;
          setFormulario((f) => ({ ...f, resultado_final: valor }));
        }}
      />

      <TextoExplicativo aberto={acordeaoAberto} aoMudar={mudarAcordeao} />

      {/* Consulta não vê botão de ação (`admin.html:3395`). O botão NÃO trava durante o envio, como
          no legado (`5308-5337`): dois cliques rápidos gravam duas vezes, e o `upsert` deixa uma linha. */}
      {!somenteLeitura && (
        <div className="mt-cx-2">
          <Botao disabled={!carregado} onClick={() => void salvar()}>
            Salvar ficha de coloração
          </Botao>
        </div>
      )}
      <LinhaDeStatus texto={status} testid="status-coloracao" />
    </section>
  );
}
